import { useCallback, useEffect, useMemo, useState } from 'react';
import {
  businessTodayAsLocalDate,
  makePreset,
  type ReportPeriod
} from './date-range/reportPeriod';
import {
  PipelinePerformanceView,
  type PipelinePerformanceFilterOptions,
  type PipelinePerformanceFilters
} from './pipeline-performance';
import { buildPipelinePerformanceModel } from './pipelinePerformanceModel';
import { fetchCanonicalReportData, type CanonicalReportLoan } from './reporting';
import { getLoanOfficerName } from './types';
import { createComponentLogger } from '@/utils/logger';

const log = createComponentLogger('PipelinePerformanceReport', 'reports');

const DEFAULT_FILTERS: PipelinePerformanceFilters = {
  loanOfficer: 'all',
  investor: 'all',
  channel: 'all',
  product: 'all'
};

function unique(values: Array<string | null | undefined>): string[] {
  return Array.from(new Set(
    values
      .map((value) => value?.trim() || '')
      .filter((value) => value && value !== '-')
  )).sort((a, b) => a.localeCompare(b));
}

export function PipelinePerformanceReport() {
  const [loans, setLoans] = useState<CanonicalReportLoan[]>([]);
  const [period, setPeriod] = useState<ReportPeriod>(() => (
    makePreset('mtd', businessTodayAsLocalDate())
  ));
  const [filters, setFilters] = useState<PipelinePerformanceFilters>(DEFAULT_FILTERS);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [warnings, setWarnings] = useState<string[]>([]);
  const [refreshedAt, setRefreshedAt] = useState<Date | null>(null);

  const loadReport = useCallback(async () => {
    const today = businessTodayAsLocalDate();
    setIsLoading(true);
    setError(null);
    setWarnings([]);
    setPeriod((current) => (
      current.mode === 'mtd' || current.mode === 'qtd' || current.mode === 'ytd'
        ? makePreset(current.mode, today)
        : current
    ));
    try {
      const result = await fetchCanonicalReportData();
      if (result.loanError || result.payoutError) {
        const issue = result.loanError || result.payoutError;
        setError(`${issue?.message || 'Unable to load pipeline performance.'} No production totals were shown because the report could not be fully reconciled.`);
        setLoans([]);
        return;
      }

      setLoans(result.loans);
      setRefreshedAt(new Date());
      if (result.payoutWarnings.length > 0) {
        setWarnings([
          `${result.payoutWarnings.length} payout match conflict${result.payoutWarnings.length === 1 ? '' : 's'} resolved to the earliest non-null fund date.`
        ]);
      }
      log.info('Loaded canonical pipeline performance data', {
        loans: result.loans.length,
        payouts: result.payouts.length,
        payoutWarnings: result.payoutWarnings.length
      });
    } catch (loadError) {
      setLoans([]);
      setError('Unable to load pipeline performance. Refresh to try again.');
      log.error('Failed to load pipeline performance', {}, loadError as Error);
    } finally {
      setIsLoading(false);
    }
  }, []);

  useEffect(() => {
    loadReport();
  }, [loadReport]);

  const filterOptions = useMemo((): PipelinePerformanceFilterOptions => ({
    loanOfficers: unique(loans.map(getLoanOfficerName)),
    investors: unique(loans.map((loan) => loan.investor_name)),
    channels: unique(loans.map((loan) => loan.channel)),
    products: unique(loans.map((loan) => loan.rate_lock_loan_program))
  }), [loans]);

  const filteredLoans = useMemo(() => loans.filter((loan) => {
    if (filters.loanOfficer !== 'all' && getLoanOfficerName(loan) !== filters.loanOfficer) {
      return false;
    }
    if (filters.investor !== 'all' && loan.investor_name !== filters.investor) {
      return false;
    }
    if (filters.channel !== 'all' && loan.channel !== filters.channel) {
      return false;
    }
    if (filters.product !== 'all' && loan.rate_lock_loan_program !== filters.product) {
      return false;
    }
    return true;
  }), [filters, loans]);

  const model = useMemo(() => {
    if (!refreshedAt || error) {
      return null;
    }
    return buildPipelinePerformanceModel(filteredLoans, period, refreshedAt);
  }, [error, filteredLoans, period, refreshedAt]);

  return (
    <PipelinePerformanceView
      loans={filteredLoans}
      model={model}
      period={period}
      filters={filters}
      filterOptions={filterOptions}
      isLoading={isLoading}
      onPeriodChange={setPeriod}
      onFiltersChange={setFilters}
      error={error}
      warnings={warnings}
      refreshedAt={refreshedAt}
      onRefresh={loadReport}
    />
  );
}

export default PipelinePerformanceReport;
